// src/pages/Roles.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { RoleDto } from '../types/dtos'; // Assuming you have a shared Role DTO
import LoadingSpinner from '../components/LoadingSpinner';
import { Plus, Eye } from 'lucide-react';


export default function Roles() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      setError(null);
      try {
        // ✅ Fetch admin roles from .NET Admin API
        const res = await apiClient.get<RoleDto[]>('/admin/roles');
        setRoles(res.data);
        setError(null);
      } catch (err: any) {
        console.error('[Roles] Failed to load roles:', err);
        setError(err.response?.data?.message || 'Unable to load roles. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto pt-6 pb-8">

          {/* Breadcrumb (PRD Section 7: "Interactive elements with feedback") */}
          <nav className="mb-6 text-sm text-gray-500" aria-label="Breadcrumb">
            <ol className="list-none p-0 inline-flex">
              <li className="flex items-center">
                <a href="/admin" className="text-blue-600 hover:underline">Home</a>
                <svg className="fill-current w-3 h-3 mx-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z" />
                </svg>
              </li>
              <li>
                <span className="text-gray-500">Roles</span>
              </li>
            </ol>
          </nav>

          {/* PRD Section 4: "Role & Access Control" - Header Card */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Role Management</h1>
                <p className="mt-1 text-sm text-gray-500">Define custom roles with specific permissions</p>
              </div>
              <button
                onClick={() => navigate('/roles/new')} // Navigate to new role form
                className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-700 to-indigo-800 text-white rounded-lg text-sm font-medium hover:from-blue-800 hover:to-indigo-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md flex items-center justify-center transition-all"
                aria-label="Create a new role"
              >
                <Plus size={18} className="mr-2" />
                <span>Create Role</span>
              </button>
            </div>
          </div>

          {/* PRD Section 4: "Role & Access Control" - Roles Table */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            {loading ? (
              <div className="flex justify-center items-center min-h-[200px]" aria-live="polite">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700" role="alert">
                {error}
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-600">No roles found.</p>
                <p className="text-gray-600 mt-2">Create a new role to get started.</p>
                <button
                  onClick={() => navigate('/roles/new')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Create Role
                </button>
              </div>
            ) : (
              <div className="bg-white">
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 text-gray-600 font-bold border-b">
                      <tr>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Role Name</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Company</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Permissions</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Users</th>
                        <th scope="col" className="px-6 py-4 text-right text-xs uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {roles.map((role) => (
                        <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-gray-900">{role.roleName}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[200px]">{role.description}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">{role.company || 'Global'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 font-medium">
                            {role.permissions?.length || 0} Permissions
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 font-medium">
                            {role.assignedUsers?.length || 0} Users
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <button
                              onClick={() => navigate(`/roles/${role.id}`)}
                              className="text-blue-600 hover:text-blue-800 font-bold"
                              aria-label={`View details for role ${role.roleName}`}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                  {roles.map((role) => (
                    <div key={role.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{role.roleName}</h4>
                          <p className="text-[10px] text-gray-400 font-bold uppercase">{role.company || 'Global Access'}</p>
                        </div>
                        <button
                          onClick={() => navigate(`/roles/${role.id}`)}
                          className="p-2 text-blue-600 bg-blue-50 rounded-lg"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 font-medium mt-1 mb-3 line-clamp-1">{role.description}</p>
                      <div className="grid grid-cols-2 gap-2 p-2 bg-gray-50 rounded">
                        <div className="text-center border-r border-gray-200">
                          <p className="text-[9px] text-gray-400 font-bold uppercase">Permissions</p>
                          <p className="text-xs text-blue-900 font-bold">{role.permissions?.length || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] text-gray-400 font-bold uppercase">Users</p>
                          <p className="text-xs text-blue-900 font-bold">{role.assignedUsers?.length || 0}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
}